import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import HomePage from './pages/HomePage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import OrderTrackingPage from './pages/OrderTrackingPage';
import OrdersPage from './pages/OrdersPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ProfilePage from './pages/ProfilePage';
import NotFoundPage from './pages/NotFoundPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminMenu from './pages/admin/AdminMenu';
import AdminDeliveryPostcodes from './pages/admin/AdminDeliveryPostcodes';
import AdminPrinterSettings from './pages/admin/AdminPrinterSettings';
import AdminSuggestedProducts from './pages/admin/AdminSuggestedProducts';
import AdminSettings from './pages/admin/AdminSettings';
import AdminModifierGroups from './pages/admin/AdminModifierGroups';
import LandingPage from './pages/landing/LandingPage';
import FinanceLoginPage from './pages/finance/FinanceLoginPage';
import FinanceDashboard from './pages/finance/FinanceDashboard';
import PosLoginPage from './pages/pos/PosLoginPage';
import PosLayout from './pages/pos/PosLayout';
import PosOrdersPage from './pages/pos/PosOrdersPage';
import PosPrinterPage from './pages/pos/PosPrinterPage';
import PosAlertsPage from './pages/pos/PosAlertsPage';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { AdminRoute } from './components/custom/AdminRoute';
import { PosRoute } from './components/custom/PosRoute';
import { FinanceRoute } from './components/custom/FinanceRoute';
import { ErrorBoundary } from './components/custom/ErrorBoundary';
import { enableContentProtection } from './utils/protectContent';
import { RootRedirect } from './components/custom/RootRedirect';
import './App.css';

// Install right-click + DevTools-shortcut blocking as early as possible.
// This is deterrence, not real security — see protectContent.js docstring.
enableContentProtection();

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <CartProvider>
            <div className="App">
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<RootRedirect><HomePage /></RootRedirect>} />
                  {/* SEO landing pages — indexable, config-driven (see src/pages/landing/topics.js). */}
                  <Route path="/fried-chicken-forfar" element={<LandingPage />} />
                  <Route path="/burgers-forfar" element={<LandingPage />} />
                  <Route path="/kebabs-forfar" element={<LandingPage />} />
                  <Route path="/takeaway-delivery-forfar" element={<LandingPage />} />
                  <Route path="/desserts-forfar" element={<LandingPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/order-success" element={<OrderSuccessPage />} />
                  <Route path="/order-tracking/:orderId" element={<OrderTrackingPage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
                  <Route path="/admin/login" element={<AdminLoginPage />} />
                  <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                  <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
                  <Route path="/admin/menu" element={<AdminRoute><AdminMenu /></AdminRoute>} />
                  <Route path="/admin/delivery-postcodes" element={<AdminRoute><AdminDeliveryPostcodes /></AdminRoute>} />
                  <Route path="/admin/printer-settings" element={<AdminRoute><AdminPrinterSettings /></AdminRoute>} />
                  <Route path="/admin/suggested-products" element={<AdminRoute><AdminSuggestedProducts /></AdminRoute>} />
                  <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
                  <Route path="/admin/modifier-groups" element={<AdminRoute><AdminModifierGroups /></AdminRoute>} />
                  <Route path="/finance" element={<Navigate to="/finance/login" replace />} />
                  <Route path="/finance/login" element={<FinanceLoginPage />} />
                  <Route path="/finance/dashboard" element={<FinanceRoute><FinanceDashboard /></FinanceRoute>} />
                  <Route path="/pos/login" element={<PosLoginPage />} />
                  <Route path="/pos" element={<PosRoute><PosLayout /></PosRoute>}>
                    <Route index element={<Navigate to="/pos/orders" replace />} />
                    <Route path="orders" element={<PosOrdersPage />} />
                    <Route path="printer" element={<PosPrinterPage />} />
                    <Route path="alerts" element={<PosAlertsPage />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </BrowserRouter>
              <Toaster position="top-center" richColors />
            </div>
          </CartProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
